<script lang="ts">
  import { candidates, activeCandidateId, currentMode } from '../lib/state';
  import { summarizeCandidate, timeClass } from '../lib/scoring';
  import type { CommuteLeg } from '../lib/types';
  import TripsPanel from './TripsPanel.svelte';
  import AmenitiesPanel from './AmenitiesPanel.svelte';
  import Rankings from './Rankings.svelte';

  $: active = $candidates.find((c) => c.id === $activeCandidateId) || null;
  $: rows = active ? active.results[$currentMode] ?? null : null;
  // Default-sort by time, fastest first; unreachable ("no route") rows last.
  $: sortedRows = rows
    ? [...rows].sort((a, b) => (a.minutes ?? Infinity) - (b.minutes ?? Infinity))
    : [];
  $: summary = active ? summarizeCandidate(active, $currentMode) : null;
  $: validCount = rows ? rows.filter((r) => r.minutes != null).length : 0;

  function worstFriend(rs: CommuteLeg[]): string {
    let max = -1;
    let name = '';
    for (const r of rs) {
      if (r.minutes != null && r.minutes > max) { max = r.minutes; name = r.name || r.friend || ''; }
    }
    return name;
  }
</script>

{#if !active}
  <div class="empty-state">
    {#if $candidates.length === 0}No candidates yet. Add an address above, or drop one on the map.
    {:else}Click any candidate (in the list or on the map) to see its full breakdown.{/if}
  </div>
{:else if !rows || rows.length === 0}
  <div class="loading">Computing travel times...</div>
{:else}
  {#if summary}
    <div class="summary-stats">
      <div class="stat-box">
        <div class="stat-label">Avg time</div>
        <div class="stat-value {summary.avgClass}">{summary.avg} min</div>
        <div class="stat-detail">across {validCount} friends</div>
      </div>
      <div class="stat-box">
        <div class="stat-label">Worst case</div>
        <div class="stat-value {summary.worstClass}">{summary.worst} min</div>
        <div class="stat-detail">{worstFriend(rows)}</div>
      </div>
    </div>
  {/if}

  <table class="results-table">
    <thead>
      <tr><th>Friend</th><th style="text-align:right">Time</th><th style="text-align:right">Distance</th></tr>
    </thead>
    <tbody>
      {#each sortedRows as row}
        {#if row.minutes == null}
          <tr><td>{row.name ?? row.friend ?? ''}</td><td colspan="2" style="color:var(--warn); font-size: 11px;">no route</td></tr>
        {:else}
          <tr>
            <td>{row.name ?? row.friend ?? ''}</td>
            <td class="time-cell {timeClass(row.minutes, $currentMode)}">{row.minutes} min</td>
            <td class="time-cell" style="color: var(--ink-soft); font-size: 11px;">{row.text ?? row.distance ?? ''}</td>
          </tr>
        {/if}
      {/each}
    </tbody>
  </table>
  <p class="help-text" style="margin-top: 10px;">
    {#if $currentMode === 'DRIVING' || $currentMode === 'TRANSIT'}
      Times computed for next Friday at 6pm (realistic going-to-see-friends time).
    {:else}Times reflect typical conditions.{/if}
  </p>

  <TripsPanel candidate={active} />
  <AmenitiesPanel candidate={active} />
  {#if $candidates.length > 1}<Rankings />{/if}
{/if}
